import React, { useCallback, useEffect, useState } from "react";
import {
  createChecklistComment,
  deleteChecklistComment,
  getChecklistComments,
  updateChecklistComment,
} from "../API";
import {
  commentErrorMessage,
  getCurrentUserId,
  isOwnComment,
} from "../utils/checklistComments";
import ChecklistCommentComposer from "./ChecklistCommentComposer";
import ChecklistCommentItem from "./ChecklistCommentItem";
import DeleteCommentConfirmModal from "./modal/DeleteCommentConfirmModal";
import "../assets/styles/checklist-comments.scss";

const GENERIC_ERROR_MESSAGE = "Could not save your comment. Please try again.";

// Per-name local fallback copy, used only when the server's own `message`
// is absent (defense in depth — the server's message survives production
// today per ErrorHandlerMiddleware, TRD §3.6). Anything unrecognised falls
// through to GENERIC_ERROR_MESSAGE (TRD §3.9).
const NAME_FALLBACK_MESSAGES = {
  CommentForbiddenError: "You don't have permission to make this change.",
  CommentNotFoundError: "This comment no longer exists.",
  CommentInvalidError: "Enter a valid comment (1–2000 characters).",
  CommentStaleError: "This comment was changed by someone else. Please try again.",
};

const resolveErrorMessage = (error) => {
  const name = error?.response?.data?.name;
  const localFallback = NAME_FALLBACK_MESSAGES[name] || GENERIC_ERROR_MESSAGE;
  return commentErrorMessage(error, localFallback);
};

// `baseApi.js`'s interceptor resolves (rather than rejects) an UNNAMED
// 4xx/401/500 by returning a toast() call's return value, `true`, or the
// raw `err` object (TRD §1.1 finding 2, NG21) — none of which is a usable
// axios response. Distinguishing a genuine 2xx response from one of those
// "quiet failure" shapes is what lets a write that silently swallowed its
// error still surface as a failure here, with the generic fallback, rather
// than being treated as a successful save.
const isUsableApiResponse = (res) =>
  Boolean(res) &&
  typeof res === "object" &&
  typeof res.status === "number" &&
  res.status < 400;

// Mirrors ChecklistNotesHistory.js:22-31's author resolution exactly.
// Duplicated locally (that file exports neither helper and NG16 marks it
// off-limits for edits) — the same small-helper duplication precedent
// ChecklistCommentItem.js (CLCMT-F005) already uses.
const actorDisplayName = (comment) => {
  const user = comment?.created_by_user;
  const name = (
    user?.name ||
    [user?.first_name, user?.last_name].filter(Boolean).join(" ")
  ).trim();
  return name || "Unknown user";
};

/**
 * The panel body rendered inside `#commentsPanel`'s offcanvas (TRD §3.9).
 * Self-fetching, directly modelled on ChecklistNotesHistory.js — including
 * its `requestIdRef` stale-response guard — and owns every piece of local
 * state a comment list needs: the list itself, which row is being edited,
 * which row is pending delete, and the composer's post state. Issues its
 * own API calls; NG10 forbids Redux/thunks for this feature.
 *
 * `canModerate` (CLCMT-F010, P1/F8) is the frontend's OWN role check —
 * `localStorage.getItem("role_name") === "Admin"` — never the backend's
 * `role.name` (`'administrator'`, a different namespace in a different
 * repo; the two are never compared cross-repo). It only controls whether
 * the Delete control is offered on someone else's comment; the server is
 * the real authority, keying its own check on `role_id === 1` (D11) and
 * re-deciding independently on every DELETE (AC-F8.3). Edit is `isOwn`-only
 * everywhere in this tree and never reads `canModerate` (NG7, AC-F8.5).
 *
 * One editor open at a time (AC-F4.8): switching to a different row's
 * editor while one is already open confirms via `window.confirm` first
 * (the same browser-native pattern `ManageChecklist.js`'s `deleteRow`
 * already uses) — a safe superset of "only prompt if there are unsaved
 * changes", since ChecklistCommentItem owns its draft text locally and the
 * container has no visibility into whether it has been touched.
 *
 * Props:
 * - projectCheckListId: number
 * - onCountChange:      (nextCount: number) => void — called with the
 *                        fresh list length after every successful create
 *                        or delete (AC-F2.6, AC-F5.5, R4)
 */
const ChecklistComments = ({ projectCheckListId, onCountChange }) => {
  const [comments, setComments] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  // Guards a fetch that resolves after a newer one has already started from
  // clobbering fresher state with a stale response (AC-F3.12).
  const requestIdRef = React.useRef(0);

  const [isPosting, setIsPosting] = useState(false);
  const [postError, setPostError] = useState(null);
  // The composer is hidden behind an "Add a comment" button until the user
  // chooses to write one; it collapses back to the button after a successful
  // post (a failed post keeps it open so the draft and error survive).
  const [showComposer, setShowComposer] = useState(false);
  // Bumped after every successful post so `<ChecklistCommentComposer
  // key={composerResetToken} .../>` remounts with a cleared draft — the
  // composer deliberately never clears itself (see its own docstring).
  const [composerResetToken, setComposerResetToken] = useState(0);

  const [editingId, setEditingId] = useState(null);
  const [editError, setEditError] = useState(null);
  const [isSavingEdit, setIsSavingEdit] = useState(false);
  // CLCMT-F011/F10: set after a 409 CommentStaleError on the currently
  // editing row. { currentText, currentEditedAt } from a follow-up refetch
  // — the error body itself carries neither (TR5, ErrorHandlerMiddleware
  // drops custom properties).
  const [staleConflict, setStaleConflict] = useState(null);

  const [pendingDelete, setPendingDelete] = useState(null);
  const [deleteError, setDeleteError] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const currentUserId = getCurrentUserId();
  // CLCMT-F010: an Admin may delete any comment, never edit one they didn't
  // author (NG7, AC-F8.5). Re-read on every render rather than memoized —
  // cheap localStorage lookup, and it must reflect a role change without
  // requiring a remount.
  const canModerate = localStorage.getItem("role_name") === "Admin";

  // `silent: true` (CLCMT-F011) skips the isLoading/loadError toggling so
  // the currently-rendered list — including whichever row has its editor
  // open — never unmounts during the refetch. That matters here
  // specifically: the ordinary (non-silent) refetch below replaces the
  // whole `<ol>` with "Loading comments…" while in flight, which would
  // unmount ChecklistCommentItem and wipe its local `draft` state exactly
  // when a 409/404 needs that draft preserved (AC-F10.3). Every other call
  // site (mount, Retry, and the post-write refetches on the happy path)
  // keeps the ordinary non-silent behaviour unchanged.
  const fetchComments = useCallback(async ({ silent = false } = {}) => {
    if (!projectCheckListId) {
      if (!silent) {
        setComments([]);
        setIsLoading(false);
        setLoadError(false);
      }
      return undefined;
    }

    const requestId = requestIdRef.current + 1;
    requestIdRef.current = requestId;
    if (!silent) {
      setIsLoading(true);
      setLoadError(false);
    }
    try {
      const res = await getChecklistComments(projectCheckListId);
      if (requestId !== requestIdRef.current) return undefined;
      if (!isUsableApiResponse(res)) {
        if (!silent) setLoadError(true);
        return undefined;
      }
      const list = Array.isArray(res.data) ? res.data : [];
      setComments(list);
      return list;
    } catch (error) {
      if (requestId !== requestIdRef.current) return undefined;
      if (!silent) {
        console.error("Failed to fetch checklist comments:", error);
        setLoadError(true);
      }
      return undefined;
    } finally {
      if (!silent && requestId === requestIdRef.current) {
        setIsLoading(false);
      }
    }
  }, [projectCheckListId]);

  useEffect(() => {
    fetchComments();
  }, [fetchComments]);

  const handlePost = async (trimmedText) => {
    setIsPosting(true);
    setPostError(null);
    try {
      const res = await createChecklistComment({
        project_check_list_id: projectCheckListId,
        comment: trimmedText,
      });
      if (!isUsableApiResponse(res)) {
        throw new Error("checklist_comment_create_unusable_response");
      }
      const fresh = await fetchComments();
      if (Array.isArray(fresh)) onCountChange?.(fresh.length);
      setComposerResetToken((token) => token + 1);
      setShowComposer(false);
    } catch (error) {
      setPostError(resolveErrorMessage(error));
    } finally {
      setIsPosting(false);
    }
  };

  const handleStartEdit = (id) => {
    if (editingId !== null && editingId !== id) {
      // eslint-disable-next-line no-alert
      const shouldSwitch = window.confirm(
        "Discard your unsaved changes to this comment?"
      );
      if (!shouldSwitch) return;
    }
    setEditError(null);
    // A dangling staleConflict from the row being switched away from must
    // not leak onto the newly-opened row — both are keyed off the same
    // editingId, so it has to be cleared here, not just in
    // handleCancelEdit (CLCMT-F011).
    setStaleConflict(null);
    setEditingId(id);
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditError(null);
    setStaleConflict(null);
  };

  // `expectedEditedAt` is the edited_at ChecklistCommentItem's editor was
  // opened against (or the fresh value from `staleConflict` on a retry) —
  // CLCMT-F011/F10, AC-F10.1. A request without the field (P0, before this
  // task) behaved as last-write-wins; this always sends it now, including
  // `null` for a never-edited comment (D8).
  const handleSaveEdit = async (id, trimmedText, expectedEditedAt) => {
    setIsSavingEdit(true);
    setEditError(null);
    setStaleConflict(null);
    try {
      const res = await updateChecklistComment(id, {
        comment: trimmedText,
        expected_edited_at: expectedEditedAt ?? null,
      });
      if (!isUsableApiResponse(res)) {
        throw new Error("checklist_comment_update_unusable_response");
      }
      await fetchComments();
      setEditingId(null);
    } catch (error) {
      const name = error?.response?.data?.name;
      if (name === "CommentStaleError") {
        // TR5: the 409 body carries no custom properties — the current
        // server text rides a follow-up GET instead (AC-F10.3). Silent so
        // the still-open editor (and its preserved draft) doesn't unmount
        // mid-refetch; editingId is left untouched too.
        const fresh = await fetchComments({ silent: true });
        const freshRow = Array.isArray(fresh)
          ? fresh.find((row) => row.id === id)
          : null;
        setStaleConflict({
          currentText: freshRow?.comment ?? "",
          currentEditedAt: freshRow?.edited_at ?? null,
        });
        setEditError(resolveErrorMessage(error));
      } else if (name === "CommentNotFoundError") {
        // AC-F10.4: this exact copy, not the generic fallback chain. Silent
        // so the message actually renders (a non-silent refetch would
        // replace the whole list with "Loading comments…" in the same
        // tick, before this message ever painted) and stays visible until
        // the refetch resolves and excludes the now-soft-deleted row,
        // which unmounts this item — and its message — naturally.
        setEditError("This comment no longer exists");
        await fetchComments({ silent: true });
      } else {
        setEditError(resolveErrorMessage(error));
      }
    } finally {
      setIsSavingEdit(false);
    }
  };

  const handleRequestDelete = (comment) => {
    setDeleteError(null);
    setPendingDelete(comment);
  };

  const handleCancelDelete = () => {
    if (isDeleting) return;
    setPendingDelete(null);
    setDeleteError(null);
  };

  const handleConfirmDelete = async () => {
    if (!pendingDelete) return;
    setIsDeleting(true);
    setDeleteError(null);
    try {
      const res = await deleteChecklistComment(pendingDelete.id);
      if (!isUsableApiResponse(res)) {
        throw new Error("checklist_comment_delete_unusable_response");
      }
      const fresh = await fetchComments();
      if (Array.isArray(fresh)) onCountChange?.(fresh.length);
      setPendingDelete(null);
    } catch (error) {
      setDeleteError(resolveErrorMessage(error));
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <div className="checklist-comments">
      {isLoading ? (
        <p className="checklist-comments-status" aria-live="polite">
          Loading comments…
        </p>
      ) : loadError ? (
        <div className="checklist-comments-error" role="alert">
          <span>Could not load comments.</span>
          <button
            type="button"
            className="btn btn-secondary checklist-comments-retry"
            onClick={fetchComments}
          >
            Retry
          </button>
        </div>
      ) : comments.length === 0 ? (
        <p className="checklist-comments-status">No comments yet.</p>
      ) : (
        // Server order rendered verbatim — never re-sorted client-side
        // (AC-F3.2).
        <ol className="checklist-comment-list">
          {comments.map((comment) => (
            <ChecklistCommentItem
              key={comment.id}
              comment={comment}
              isOwn={isOwnComment(comment, currentUserId)}
              canModerate={canModerate}
              isEditing={editingId === comment.id}
              isSaving={editingId === comment.id && isSavingEdit}
              editError={editingId === comment.id ? editError : null}
              staleConflict={editingId === comment.id ? staleConflict : null}
              onStartEdit={() => handleStartEdit(comment.id)}
              onCancelEdit={handleCancelEdit}
              onSaveEdit={(trimmedText, expectedEditedAt) =>
                handleSaveEdit(comment.id, trimmedText, expectedEditedAt)
              }
              onRequestDelete={() => handleRequestDelete(comment)}
            />
          ))}
        </ol>
      )}

      {showComposer ? (
        <ChecklistCommentComposer
          key={composerResetToken}
          onPost={handlePost}
          isSubmitting={isPosting}
          serverError={postError}
          onCancel={() => {
            setShowComposer(false);
            setPostError(null);
          }}
        />
      ) : (
        <button
          type="button"
          className="btn checklist-comments-add-btn"
          onClick={() => setShowComposer(true)}
        >
          Add a comment
        </button>
      )}

      <DeleteCommentConfirmModal
        isOpen={Boolean(pendingDelete)}
        authorName={pendingDelete ? actorDisplayName(pendingDelete) : ""}
        isModeratorDelete={Boolean(
          canModerate && pendingDelete && !isOwnComment(pendingDelete, currentUserId)
        )}
        busy={isDeleting}
        serverError={deleteError}
        onConfirm={handleConfirmDelete}
        onCancel={handleCancelDelete}
      />
    </div>
  );
};

export default ChecklistComments;
